/**
 * TipToast — balão de dica pós-busca (porta de `showTip` [application.js:4522]).
 * div.tip (fixo no body) > close + div#tip (mensagem + "tente pesquisar por: <span>…").
 * Aparece ~1,5s depois do gatilho e fecha no X.
 *
 * Movimento (23/10/2026, guia/09 "Toast"): é o toast do guia (.ds-toast + .is-aberto). Sobe
 * 8 px com fade em 200 ms e sai em 140 ms (a peça fica montada enquanto sai, com o texto da
 * última dica). Fica 4 s (--permanencia-4000): a barra fina do tempo (.ds-toast-tempo) mostra
 * quanto falta e para com o mouse em cima ou com o foco dentro; quando ela termina, a dica
 * fecha. Uma dica nova no lugar recomeça o tempo. Com "reduzir movimento": fade de 120 ms,
 * sem subir, e a barra some (o tempo continua contando).
 */
import { useEffect, useState, type AnimationEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTipStore } from './tipStore';
import { RichText } from '../../components/ui/RichText';
import { usePresenca } from '../../hooks/usePresenca';
import { useGuiaOcupado } from '../guia/guiaStore';

interface Dica {
  tip: string;
  suggestion?: string;
}

export function TipToast(): JSX.Element | null {
  const { t } = useTranslation();
  const tip = useTipStore((s) => s.tip);
  const suggestion = useTipStore((s) => s.suggestion);
  const clearTip = useTipStore((s) => s.clearTip);
  const [shown, setShown] = useState(false);
  // A última dica continua na tela enquanto o toast sai.
  const [ultima, setUltima] = useState<Dica | null>(null);
  if (tip && (tip !== ultima?.tip || suggestion !== ultima?.suggestion)) {
    setUltima({ tip, suggestion });
  }

  // Legado: aparece 1,5s após ser disparada.
  useEffect(() => {
    if (!tip) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), 1500);
    return () => clearTimeout(timer);
  }, [tip]);

  // Com o guia passo a passo ligado, a dica depois da busca não aparece por cima dele.
  const guiaNaTela = useGuiaOcupado();
  const presenca = usePresenca(Boolean(tip) && shown && !guiaNaTela);

  if (!presenca.montado || !ultima) return null;

  const html =
    ultima.tip +
    (ultima.suggestion
      ? `<br /><br />${t('tip_try_next', 'Na próxima vez, tente pesquisar por:')} <span>${ultima.suggestion}</span>`
      : '');

  // O fim da barra do tempo fecha a dica (a pausa com o mouse vale para os dois).
  const onTempoFim = (e: AnimationEvent<HTMLSpanElement>): void => {
    if (e.animationName === 'ds-tempo') clearTip();
  };

  return createPortal(
    <div className={`tip ds-toast${presenca.aberto ? ' is-aberto' : ''}`} role="status">
      <button
        type="button"
        className="close ds-resposta"
        aria-label={t('close', 'Fechar')}
        onClick={clearTip}
      >
        <span className="material-symbols-rounded middle">close</span>
      </button>
      <RichText as="div" html={html} />
      <span
        key={`${ultima.tip}|${ultima.suggestion ?? ''}`}
        className="ds-toast-tempo"
        aria-hidden="true"
        onAnimationEnd={onTempoFim}
      />
    </div>,
    document.body,
  );
}
